import { Component, OnInit, Inject, forwardRef, OnDestroy } from '@angular/core';
import { NgRedux, select } from '@angular-redux/store';
import { Observable, Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Email } from './models/email.model';
import { DatePipe, NgIf } from '@angular/common';
import { AppState } from '../../../store/state.model';
import { SpinnerService } from '../../../common/components/spinner/overlay-spinner.service';
import { SpinnerRef } from '../../../common/components/spinner/overlay-ref.service';
import { RedirectServiceService } from '../../../common/services/redirect-service/redirect-service.service';
import { SidenavService } from '../../services/sidenav.service';
import { MatSnackBar, MatDialog } from '@angular/material';
import { AdminApiHandlerService } from '../../services/api-handler/api-handler.service';
import { VehicleDispatcher } from '../../../store/vehicle-store/vehicle-dispatcher';
import { RemoteConfirmationComponent } from '../remote-confirmation/remote-confirmation.component';
import { StepDispatcher } from '../../../store/steps-store/step-dispatcher';
import { CustomerDispatcher } from '../../../store/customer/customer-dispatcher';
import { PhonePipe } from '../../../common/pipes/phone.pipe';
import { environment } from '../../../../environments/environment';
import { AdminState } from '../../../store/admin-store/admin-state.model';
import { MatFormField, MatSuffix, MatError } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-authorize-remote',
    templateUrl: './authorize-remote.component.html',
    styleUrls: ['./authorize-remote.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatInput, NgIf, MatIcon, MatSuffix, MatError, MatButton, TranslatePipe]
})
export class AuthorizeRemoteComponent implements OnInit, OnDestroy {
  /**
   * @type {Observable<RemoteState>} to observe Remote variable
   */
  @select('admin') readonly adminObservable: Observable<AdminState>;
  /**
  *@property model to store the model (Email or Phone)
  **/
  public model: Email;
  /**
  *@property customer to store the form of email or phone
  */
  public customer: UntypedFormGroup;
  /**
  *@property isNext stores true when cliked next button
  **/
  public isNext: boolean;
  /**
  *@property placeholder stores translation string for placeholder of email or phone
  **/
  public placeholder: string;
  /**
  *@property tag stores translation string for tag of email or phone
  **/
  public tag: string;
  /**
  *@property updateDetails stores the remoteStore object of email or phone
  **/
  public updateDetails: object;
  /**
  *@property translateParams stores the translateParams object of email or phone
  **/
  public translateParams: object;
  /**
  *@property fieldRequired stores the translation for required field validation error of email or phone and code
  **/
  public fieldRequired: Array<string>;
  /**
  *@property fieldPattern pattern of field
  **/
  public fieldPattern: Array<string>;
  /**
  *@property fieldLength length of field
  **/
  public fieldLength: Array<string>;
  /**
  *@property Reference to the spinner service so it can be open or close in any feature of the screen
  **/
  public spinnerRef: SpinnerRef;
  /**
  *@property to store guid and object params required for updating the address
  **/
  public updateParams: object;
  /**
  *@property to be false if the verification code is Invalid
  **/
  public errorHint: boolean;
  public subscription: Subscription;

  constructor(
    @Inject(forwardRef(() => AdminApiHandlerService)) public apiHandler: AdminApiHandlerService,
    public fb: UntypedFormBuilder,
    private sidenav: SidenavService,
    public datePipe: DatePipe,
    public spinner: SpinnerService,
    public ngRedux: NgRedux<AppState>,
    public phonePipe: PhonePipe,
    public customer_dispatcher: CustomerDispatcher,
    private readonly snackBar: MatSnackBar,
    public stepDispatcher: StepDispatcher,
    public dialog: MatDialog,
    public redirectService: RedirectServiceService,
    public vehicleDispatcher: VehicleDispatcher,
  ) {
  }
  /**
   * @function ngOnDestroy
   */
  ngOnDestroy() {
    if (!!this.subscription) {
      this.subscription.unsubscribe();
    }
  }
  /**
   * @function ngOnInit Method to Initializes the component and perform initial operations
   */
  ngOnInit(): void {
    this.subscription = this.adminObservable.subscribe((adminStore) => {
      this.updateDetails = !!adminStore ? adminStore : {};
      this.initialize();
    });
    this.isNext = true;
    this.errorHint = false;
  }
  /**
   * @function initialize initi methods
   */
  public initialize(): void {
    this.model = new Email(this.updateDetails);
    this.customer = this.fb.group(this.model.form);
    this.customer.setValue({
      editValue: !!this.updateDetails && !!this.updateDetails['updateValue'] ? this.updateDetails['updateValue'] : this.model.values.editValue,
    });
    this.tag = this.model.tag;
    this.placeholder = this.model.placeholder;
    this.translateParams = this.model.translateParams;
    this.fieldRequired = this.model.fieldRequired;
    this.fieldPattern = this.model.fieldPattern;
    this.fieldLength = this.model.fieldLength;
  }
  /**
   * @method onKeyUp updates the form value
   * @returns void
   **/
  public onKeyUp(): void {
    this.customer.controls.editValue.setValue(this.customer.controls.editValue.value.replace(/ /g, '').trim());
    this.customer.controls.editValue.updateValueAndValidity();
  }
  /**
  * @function autoAuthorize auto authorize
  **/
  public autoAuthorize(): void {
    this.sidenav.toggle();
    this.spinnerRef = this.spinner.open();
    this.apiHandler.postRequest(this.model.auto_authorize, this.model.apiPayload(),
      this.model.getApiParams()).subscribe({
      next: () => this.sendCodesuccessCallback(),
      error: (error) => this.sendCodeFailure(error)
    }
      );
  }
  /**
   * @function sendCodesuccessCallback will be called after sendCode call was successful
   * @Param {object} response of the API
   **/
  public sendCodesuccessCallback(): void {
    this.confirmation('remote-authorize', true);
    this.spinnerRef.close();
    this.getVinSubscription();
  }
  /**
  * @function sendCodeFailure Action for error after API response
  * @param {object} error  Response of the API
  **/
  public sendCodeFailure(error: object): void {
    this.confirmation('remote-authorize', false);
    this.errorCallback(error);
  }
  /**
   * @function getVinSubscription get vin subscription from vin list
   */
  public getVinSubscription(): void {
    const vehicle = this.ngRedux.getState().selectedVehicle.vehicle;
    const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(environment.admin.apis.subscription.vinlist, {
      type: 'vehicle_list', params: {
        vin: vehicle['vin'],
        generation: vehicle['generation'],
        brand: vehicle['brand']
      }
    }).subscribe({
      next: (res) => {
        spinnerRef.close();
        this.successGetVinSubscription(res.payload[0]);
      },
      error: (error) => {
        this.showErrorComponent(!!error && !!error.errorCode ? error : { errorCode: 'UNKNOWN_VINLIST' });
        spinnerRef.close();
      }
    });
  }
  /**
 * @function successGetVinSubscription call back for vinsubscription success
 */
  successGetVinSubscription(payload: object) {
    this.vehicleDispatcher.setRemoteDetails({
      remoteUserGuid: payload['remoteUserGuid'],
      remoteSubscriptionStatus: payload['remoteSubscriptionStatus']
    });
  }
  /**
  * @function errorCallback Action for error after API response
  * @param {object} error  Response of the API
  **/
  public errorCallback(error: object): void {
    this.spinnerRef.close();
    this.confirmation('remote-authorize', false);
    error['errorCode'] === 'AVCD-0006' ? this.errorHint = true : error['errorCode'] === 'SUB-0015' ? this.confirmation('unable-to-update', false) :
      this.showErrorComponent(error);
  }
  /**
 * @function showErrorComponent for un-successful on fetching vehicle details for given Vin
 * @param {object} error error object
 */
  private showErrorComponent(error: Object): void {
    this.redirectService.showError(error['errorCode']);
    this.stepDispatcher.setNextStep('error');
  }
  /**
  * @function back will set the isNext to true and closes the side nav when called
  **/
  public back(): void {
    this.isNext = true;
    this.sidenav.toggle();
  }
  /**
  * @function confirmation should call confirmation component when called
  * @param updateValue of type string
  **/
  public confirmation(updateValue: string, success: boolean): void {
    window.scrollTo(0, 0);
    this.snackBar.openFromComponent(RemoteConfirmationComponent, {
      data: { type: updateValue, success },
      duration: 6000,
      verticalPosition: 'top'
    });
  }
}
